import { toaster } from "@/components/ui/toaster";
import {
  InformeUploadResponse,
  postInformeMargenBruto,
  putInformeMargenBruto,
} from "@/services/gross-margin-requests.service";
import { uploadFileToStorage } from "@/services/file.service";
import { AppColors } from "@/styles/colors";
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Portal,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { CheckCircle, Upload, X } from "lucide-react";
import { Dispatch, DragEvent, SetStateAction, useRef, useState } from "react";
import { LightMode } from "@/components/ui/color-mode";

const MAX_FILE_SIZE_MB = 20;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_EXT = [".pdf", ".xls", ".xlsx"];

function validateFile(file: File): string | null {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `El archivo no puede superar los ${MAX_FILE_SIZE_MB}MB.`;
  }
  const ext = "." + file.name.split(".").pop()?.toLowerCase();
  if (!ACCEPTED_EXT.includes(ext)) {
    return "Sólo se permiten archivos .PDF, .XLS o .XLSX.";
  }
  return null;
}

function getContentType(file: File): string {
  const ext = file.name.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf": return "application/pdf";
    case "xls": return "application/vnd.ms-excel";
    case "xlsx": return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default: return file.type || "application/octet-stream";
  }
}

type UploadStatus = "idle" | "uploading" | "success" | "error";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  idSolicitud: number;
  onAccept: (notas: string) => void;
  loading?: boolean;
}

export const InformeModal = ({ open, setOpen, idSolicitud, onAccept, loading }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [fileError, setFileError] = useState<string | null>(null);
  const [notas, setNotas] = useState("");
  const [dragging, setDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [informeId, setInformeId] = useState<number | undefined>(undefined);

  const handleFile = (f: File) => {
    const error = validateFile(f);
    if (error) {
      setFileError(error);
      setFile(undefined);
    } else {
      setFileError(null);
      setFile(f);
      setUploadStatus("idle");
      handleUpload(f);
    }
  };

  const clearFile = () => {
    setFile(undefined);
    setFileError(null);
    setUploadStatus("idle");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleClose = () => {
    setFile(undefined);
    setFileError(null);
    setNotas("");
    setUploadStatus("idle");
    setInformeId(undefined);
    setOpen(false);
  };

  const handleUpload = async (fileToUpload: File) => {
    setUploadStatus("uploading");

    const contentType = getContentType(fileToUpload);
    let uploadResult: InformeUploadResponse;

    // Step 1: register informe and get SAS URL
    try {
      let result: InformeUploadResponse | null;
      if (informeId !== undefined) {
        result = await putInformeMargenBruto({
          idSolicitud,
          idInformeMargenBruto: informeId,
          tipoArchivo: contentType,
          nombreDocumento: fileToUpload.name,
        });
      } else {
        result = await postInformeMargenBruto({
          idSolicitud,
          tipoArchivo: contentType,
          nombreDocumento: fileToUpload.name,
        });
      }
      if (!result) throw new Error();
      uploadResult = result;
    } catch {
      toaster.create({ type: "error", title: "No se pudo registrar el informe. Intentá nuevamente." });
      setUploadStatus("error");
      return;
    }

    // Step 2: upload file to SAS URL
    try {
      await uploadFileToStorage({
        uploadUrl: uploadResult.uploadUrl,
        buffer: fileToUpload,
        contentType,
      });
      setInformeId(uploadResult.idInformeMargenBruto);
      setUploadStatus("success");
    } catch {
      toaster.create({ type: "error", title: "No se pudo subir el archivo. Intentá nuevamente." });
      setUploadStatus("error");
    }
  };

  const handleAccept = () => {
    onAccept(notas);
  };

  const isUploading = uploadStatus === "uploading";
  const isUploaded = uploadStatus === "success";

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => { if (!e.open && !isUploading) handleClose(); }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content minW="480px">
              <Dialog.Header paddingY="16px" paddingX="24px">
                <Dialog.Title fontWeight={600} fontSize="18px">
                  Adjuntar Informe
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="24px" paddingY="8px">
                <Flex flexDir="column" gap="16px">
                  <Box
                    border="2px dashed"
                    borderColor={
                      isUploaded
                        ? "green.400"
                        : dragging
                        ? AppColors.GREEN_PRIMARY
                        : "gray.300"
                    }
                    borderRadius="8px"
                    height="140px"
                    position="relative"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    cursor={isUploading || isUploaded ? "default" : "pointer"}
                    bg={isUploaded ? "green.50" : dragging ? "green.50" : "gray.50"}
                    onDrop={isUploading || isUploaded ? undefined : handleDrop}
                    onDragOver={isUploading || isUploaded ? undefined : handleDragOver}
                    onDragLeave={isUploading || isUploaded ? undefined : handleDragLeave}
                    onClick={isUploading || isUploaded ? undefined : () => inputRef.current?.click()}
                  >
                    <input
                      ref={inputRef}
                      type="file"
                      accept={ACCEPTED_EXT.join(",")}
                      style={{ display: "none" }}
                      onChange={handleInputChange}
                    />
                    {file ? (
                      <>
                        {isUploading && <Spinner size="md" color={AppColors.GREEN_PRIMARY} />}
                        {isUploaded && (
                          <Box position="absolute" top="10px" left="10px">
                            <CheckCircle size={22} color="green" />
                          </Box>
                        )}
                        {!isUploading && (
                          <Box
                            as="button"
                            position="absolute"
                            top="10px"
                            right="10px"
                            cursor="pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearFile();
                            }}
                          >
                            <X size={20} color={AppColors.GRAY_TEXT} />
                          </Box>
                        )}
                        <Text fontSize="14px" fontWeight={500} paddingX="20px">{file.name}</Text>
                      </>
                    ) : (
                      <Flex flexDir="column" alignItems="center" gap="8px">
                        <Upload size={32} color={AppColors.GRAY_TEXT} />
                        <Text fontSize="14px" color={AppColors.GRAY_TEXT}>
                          Arrastrá el archivo o hacé click
                        </Text>
                        <Text fontSize="12px" color={AppColors.LIGHT_GRAY_TEXT}>
                          .PDF, .XLS, .XLSX hasta {MAX_FILE_SIZE_MB}MB
                        </Text>
                      </Flex>
                    )}
                  </Box>

                  <Box h="18px">
                    {fileError && (
                      <Text fontSize="13px" color={AppColors.ERROR_TEXT}>
                        {fileError}
                      </Text>
                    )}
                  </Box>

                  <Flex flexDir="column" gap="4px">
                    <Textarea
                      padding={2}
                      placeholder="Ingresar notas"
                      value={notas}
                      onChange={(e) => setNotas(e.target.value)}
                      maxLength={100}
                      rows={3}
                      fontSize="14px"
                    />
                    <Text fontSize="12px" color={AppColors.LIGHT_GRAY_TEXT} textAlign="right">
                      Máximo 100 caracteres
                    </Text>
                  </Flex>
                </Flex>
              </Dialog.Body>

              <Dialog.Footer paddingY="16px" paddingX="24px">
                <Button
                  variant="outline"
                  paddingX="16px"
                  fontWeight={600}
                  onClick={handleClose}
                  disabled={isUploading || loading}
                >
                  Cancelar
                </Button>
                <Button
                  paddingX="16px"
                  bgColor={AppColors.GREEN_PRIMARY}
                  fontWeight={600}
                  onClick={handleAccept}
                  loading={loading}
                  disabled={!isUploaded || loading}
                >
                  Aceptar
                </Button>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
